import React from 'react'

import NiceModal from '@ebay/nice-modal-react'
import {
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
} from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { FaIcon } from '#components/FaIcon'
import { useModalControls } from '#components/Modals'
import { Toast } from '#components/Toast'
import { ILoadedScript } from '#state'

import { useBulkReview } from '../hooks'

export type Params = { script: ILoadedScript; canReviewTrackedChanges: boolean }

const BulkReview = observer(({ script, canReviewTrackedChanges }: Params) => {
  const controls = useModalControls()

  const bulkReview = useBulkReview({
    script,
    onSuccess: () => {
      script.disableChangeTracking()
      controls.onClose()
    },
  })

  const rawCount = script.changeTrackingData.proposedChanges.length
  const count = ` (${rawCount})`
  const errored = bulkReview.data?.some((res) => res.status === 'rejected')

  return (
    <Modal
      title="Disable change-tracking"
      opened={controls.opened}
      onClose={controls.onClose}
    >
      <LoadingOverlay visible={bulkReview.isPending} />
      <Stack>
        {!canReviewTrackedChanges && (
          <Toast
            type="warning"
            message="You don't have permission to review"
            dismissable={false}
          ></Toast>
        )}
        <Text>
          All proposed changes must be reviewed prior to disabling
          change-tracking.
        </Text>
        <Group justify="center">
          <Button
            disabled={!canReviewTrackedChanges}
            variant="outline"
            c="green.8"
            onClick={() => bulkReview.mutate('approve')}
            leftSection={<FaIcon faIconSet="fas" icon="fa-check" />}
          >
            {`Approve all ${count}`}
          </Button>
          <Button
            disabled={!canReviewTrackedChanges}
            onClick={() => bulkReview.mutate('reject')}
            variant="danger"
            leftSection={<FaIcon faIconSet="fas" icon="fa-ban" />}
          >
            {`Decline all ${count}`}
          </Button>
        </Group>
        {errored && (
          <Toast
            type="error"
            message="One or more tracked changes couldn't be reviewed"
          />
        )}
        <Group justify="center">
          <Button onClick={controls.onClose} variant="outline">
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
})

export const BulkReviewModal = NiceModal.create<Params>(BulkReview)
